import h from 'hyperscript';

import checkSmallIcon from '@disy/cadenza-icons/check-small.svg';
import closeIcon from '@disy/cadenza-icons/close.svg';
import errorIcon from '@disy/cadenza-icons/error.svg';
import infoIcon from '@disy/cadenza-icons/info.svg';
import warningIcon from '@disy/cadenza-icons/warning.svg';
import type { StyleClass } from '@disy/lib/add-style-class';
import { addStyleClass } from '@disy/lib/add-style-class';
import { executeAfterAnimation } from '@disy/lib/animation-util';
import type { Icon } from '@disy/lib/icon';
import { icon } from '@disy/lib/icon';
import { createIconButton } from '@disy/ui/button';
import type { Content } from '@disy/ui/content';

import './toast-message.css';

const COMPONENT_NAME = 'd-toast-message';
const DEFAULT_FADE_OUT_TIME_MS = 6000;
const MESSAGE_PARENT_CLASS = 'd-toast-message-parent';

const ICONS: Record<ToastMessageType, string> = {
  info: infoIcon,
  success: checkSmallIcon,
  warning: warningIcon,
  error: errorIcon,
};

type ToastMessageType = 'info' | 'success' | 'warning' | 'error';
const darkBgType: ToastMessageType[] = ['error', 'success', 'info'];

interface ToastMessageOptions {
  /** The message type. Default=info*/
  type?: ToastMessageType;
  /**
   * The HTML element in whose bottom right corner the toast message is going to be displayed.
   * If unspecified the message is added in the standard toast message area. *
   */
  parentElement?: HTMLElement;
  /** If true, the message automatically fades out after fadeOutTime. Default=true*/
  fadeOut?: boolean;
  /** If set, fadeout will start after the given amount of time in ms. If not, the default will be used.*/
  fadeOutTimeMs?: number;
  /** If true, a close button is added so the use can manually close the message. Default=false*/
  closeable?: boolean;
  /** Specify an icon to override the default icons.*/
  icon?: Icon;
  /** Specify an additional CSS class to add to the toast message elements */
  styleClass?: StyleClass;
}

/**
 * Shows a toast message.
 *
 * When the message is closed, whether because of time out or a user action,
 * a "close" CustomEvent gets dispatched.
 *
 * @param content - message content
 * @param [options] - Options
 * @return toast message
 *
 * Note: In order to trigger the display of a toast message on the main page from inside an iframe,
 * use the post message api (for example workbookMapIframeApi.showToastMessage()).
 */
export function showToastMessage(content: Content, options: ToastMessageOptions = {}) {
  const message = new ToastMessage(content, options);
  getMessageParent(options.parentElement).prepend(message);
  return message;
}

function getMessageParent(
  scope: HTMLElement = document.querySelector('.d-modal:not([hidden], .closed)') ?? document.body,
): Element {
  let messageParent = scope.querySelector(`:scope > .${MESSAGE_PARENT_CLASS}`);
  if (!messageParent) {
    messageParent = h('.d-toast-message-parent');
    scope.append(messageParent);
  }
  return messageParent;
}

export class ToastMessage extends HTMLElement {
  _options;
  _content;

  constructor(content: Content, options: ToastMessageOptions) {
    super();
    this._content = content;
    this._options = options;
  }

  connectedCallback() {
    const {
      type = 'info',
      styleClass,
      icon: messageIcon = icon(ICONS[type]),
      closeable,
      fadeOut = true,
    } = this._options;

    this.classList.add(COMPONENT_NAME, `${COMPONENT_NAME}-${type}`, 'faded-out');
    this.classList.toggle('dark-bg', darkBgType.includes(type));
    addStyleClass(this, styleClass);

    this.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
    this.append(messageIcon, h(`.${COMPONENT_NAME}--text`, this._content));

    if (closeable) {
      this.append(this._createCloseButton());
    }

    setTimeout(() => {
      if (fadeOut) {
        this._fadeOut();
      }
      this.classList.remove('faded-out');
    });
  }

  _fadeOut() {
    executeAfterAnimation(this, () => {
      const { fadeOutTimeMs = DEFAULT_FADE_OUT_TIME_MS } = this._options;
      setTimeout(() => {
        this.classList.add('faded-out');
        executeAfterAnimation(this, () => this._close());
      }, fadeOutTimeMs);
    });
  }

  _createCloseButton() {
    return createIconButton(icon(closeIcon), 'close', {
      variant: 'borderless',
      size: 'xs',
      styleClass: 'close-button',
      onclick: () => this._close(),
    });
  }

  _close() {
    this.dispatchEvent(new CustomEvent('close'));
    this.remove();
  }
}

customElements.define(COMPONENT_NAME, ToastMessage);
